import { useContext, useEffect, useLayoutEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';

import axios from '../axios';

import { getUserRoles, hasPermission, includesRole } from '../helpers/user';

import { setNotificationList } from '../redux/actions/notification';

import ComponentProvider from '../components/ComponentProvider/index.jsx';
import Error403 from '../components/Error403/index.jsx';
import { errorToastHandler, infoToastHandler, warnToastHandler } from '../components/action_notifier.jsx';
import Footer from '../components/footer/footer.jsx';
import Header from '../components/header/header.jsx';
import Sidebar from '../components/sidebar/sidebar.jsx';

import DefaultRedirect from './DefaultRedirect.jsx';

import { SocketContext } from '../context/socket';
import ThemeRoutes from '../routes/routing.jsx';

const priorityToastHandlerMapping = {
  low: infoToastHandler,
  medium: warnToastHandler,
  high: errorToastHandler
};

const FullLayout = props => {
  const location = useLocation();
  const history = useHistory();
  const { socket } = useContext(SocketContext);

  const [width, setWidth] = useState(window.innerWidth);
  const [settings, setSettings] = useState([
    {
      theme: 'light',
      layout: 'vertical',
      dir: 'ltr',
      sidebartype: 'mini-sidebar', // by default the sidebar should be collapsed
      sidebarpos: 'fixed',
      headerpos: 'fixed',
      boxed: 'full',
      navbarbg: 'skin6',
      sidebarbg: 'skin1',
      logobg: 'skin1'
    }
  ]);

  const roles = getUserRoles(props.user?.roles);

  useEffect(() => {
    if (window.innerWidth < 767 && document.getElementById('main-wrapper').className.indexOf('show-sidebar') !== -1) {
      document.getElementById('main-wrapper').classList.toggle('show-sidebar');
    }
  }, [history]);

  useEffect(() => {
    if (socket) {
      socket.on('notification', ({ message, priority }) => {
        priorityToastHandlerMapping[priority](message);

        getNotifications();
      });
    }
  }, [socket]);

  const getNotifications = async () => {
    const response = await axios.get('notifications', {
      params: {
        query: 'notification_badge'
      }
    });
    props.setNotificationList(response.data);
  };

  useLayoutEffect(() => {
    window.addEventListener('load', updateDimensions);
    window.addEventListener('resize', updateDimensions);
    return () => {
      window.removeEventListener('resize', updateDimensions);
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  /*--------------------------------------------------------------------------------*/
  /*Function that handles sidebar, changes when resizing App                        */
  /*--------------------------------------------------------------------------------*/
  const updateDimensions = () => {
    const element = document.getElementById('main-wrapper');

    setWidth(window.innerWidth);
    element?.setAttribute('data-sidebartype', 'mini-sidebar');
    element?.classList.add('mini-sidebar');

    if (settings[0].sidebarpos === 'fixed') {
      document.getElementById('sidebar-position')?.setAttribute('checked', '');
    }
    if (settings[0].headerpos === 'fixed') {
      document.getElementById('header-position')?.setAttribute('checked', '');
    }
    if (settings[0].theme === 'dark') {
      document.getElementById('theme-view')?.setAttribute('checked', '');
    }
    if (settings[0].boxed === 'boxed') {
      document.getElementById('boxed-layout')?.setAttribute('checked', '');
    }
    if (settings[0].dir === 'rtl') {
      document.getElementById('rtl')?.setAttribute('checked', '');
    }
  };

  return (
    <div
      id="main-wrapper"
      dir={settings[0].dir}
      data-theme={settings[0].theme}
      data-layout={settings[0].layout}
      data-sidebartype={settings[0].sidebartype}
      data-sidebar-position={settings[0].sidebarpos}
      data-header-position={settings[0].headerpos}
      data-boxed-layout={settings[0].boxed}>
      <Header data={{ settings: settings }} {...props} socket={socket} />
      <Sidebar data={{ settings: settings }} {...props} routes={ThemeRoutes} />
      <div className="page-wrapper d-block">
        <div className="page-content container-fluid">
          <Switch>
            <Route
              exact
              path="/"
              render={() => (
                <DefaultRedirect
                  roles={roles}
                  routes={ThemeRoutes} 
                  includesRole={includesRole}
                />
              )}
            />
            {ThemeRoutes.map((prop, index) => {
              const hasPermissionToView = prop.permissions
                ? hasPermission(prop.permissions, props.userPermissions)
                : true;

              if (prop.collapse) {
                return prop.children.map(prop2 => {
                  if (prop2.collapse) {
                    return prop2.subchild.map(prop3 => (
                      <Route
                        key={location.pathname}
                        path={prop3.path}
                        render={routeProps => {
                            if (roles?.length && prop.roles?.length && !includesRole(prop.roles, roles)) {
                              return <Redirect key={index} to="/auth/login" />;
                            }
                            return (<ComponentProvider features={prop3.features}>
                              {hasPermissionToView ? <prop3.component {...routeProps} /> : <Error403 />}
                            </ComponentProvider>
                          )}
                        }
                      />
                    ));
                  }
                  return (
                    <Route
                      key={location.pathname}
                      path={prop2.path}
                      render={routeProps => {
                        if (roles?.length && prop.roles?.length && !includesRole(prop.roles, roles)) {
                          return <Redirect key={index} to="/auth/login" />;
                        }
                        return (
                          <ComponentProvider features={prop2.features}>
                            {hasPermissionToView ? <prop2.component {...routeProps} /> : <Error403 />}
                          </ComponentProvider>
                        )
                      }}
                    />
                  );
                });
              } else {
                return (
                  <Route
                    key={location.pathname}
                    path={prop.path}
                    render={routeProps => {
                      if (roles?.length && prop.roles?.length && !includesRole(prop.roles, roles)) {
                        return <Redirect key={index} to="/auth/login" />;
                      }
                      return (
                        <ComponentProvider features={prop.features}>
                          {hasPermissionToView ? <prop.component {...routeProps} /> : <Error403 />}
                        </ComponentProvider>
                      );
                    }}
                  />
                );
              }
            })}
          </Switch>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default connect(
  state => ({
    user: state.user?.user,
    userPermissions: state?.user?.permissions || []
  }),
  dispatch => ({
    setNotificationList: id => {
      dispatch(setNotificationList(id));
    }
  })
)(FullLayout);
